import type { TranslationDictionary } from '../types'

export const en = {
  common: {
    localeLabel: 'Language',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    close: 'Close',
    loading: 'Loading…',
    none: 'None',
    or: 'or',
    default: 'Default',
    inactive: 'Inactive',
    keySet: 'Key set',
    noApiKey: 'No API key',
    tokens: 'tokens',
    requests: 'requests',
    estCost: 'Est. Cost',
    yes: 'Yes',
    no: 'No',
  },
  locale: {
    english: 'EN',
    german: 'DE',
  },
  status: {
    ACTIVE: 'Active',
    DRAFT: 'Draft',
    PAUSED: 'Paused',
    ARCHIVED: 'Archived',
  },
  landing: {
    hero: {
      badge: 'Franchise AI Agency Platform',
      title: 'Run White-Label',
      titleAccent: 'AI Franchises',
      subtitle: 'Every agency runs as a franchise workspace. Shared Ops controls hosting and channel credentials while franchises launch curated AI services on Telegram, WhatsApp, and phone.',
      ctaPrimary: 'Get Started',
      ctaSecondary: 'Open Console',
      providerSupport: 'Shared Ops · Railway-first hosting · Telegram · WhatsApp · Twilio Voice',
      scroll: 'scroll',
    },
    features: {
      eyebrow: 'Franchise Operations',
      title: 'Operate Shared Ops, launch local franchises',
      subtitle: 'A franchise-first operating model with centralized security and distributed execution.',
      multiProviderTitle: 'Shared Ops Control',
      multiProviderDescription: 'Centralized credential vault and hosting provider connections for all franchise workspaces.',
      secureTitle: 'Curated AI Offerings',
      secureDescription: 'Audience + vertical template packs for support, sales, operations, and more.',
      analyticsTitle: 'Cross-Channel Runtime',
      analyticsDescription: 'Deploy one agent across Telegram, WhatsApp, and phone channels.',
      chatTitle: 'Invoice-ready Metering',
      chatDescription: 'Track usage and revenue-share by franchise, channel, and billing period.',
    },
    cta: {
      title: 'Ready to launch your franchise AI network?',
      subtitle: 'Create franchise workspaces, connect channels, and run invoice-ready operations from Shared Ops.',
      button: 'Get Started',
      meta: 'Franchise-ready · Multi-channel · Open architecture',
      builtOn: 'Built on open source ·',
    },
  },
  auth: {
    login: {
      title: 'Sign in to your account',
      passkey: 'Sign in with Passkey',
      passkeyFailed: 'Passkey sign-in failed',
      passkeyInvalidOptions: 'Passkey sign-in is temporarily unavailable. Use email & password below.',
      useEmail: 'Use email & password',
      emailPlaceholder: 'you@example.com',
      passwordPlaceholder: 'Password',
      signIn: 'Sign In',
      invalidCredentials: 'Invalid credentials',
      signInFailed: 'Sign-in failed',
      noAccount: "Don't have an account?",
      register: 'Register',
    },
    register: {
      title: 'Create your account',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Jane Smith',
      email: 'Email',
      emailOptional: 'Email (optional)',
      emailOptionalHint: 'If added, we will use it to label your passkey and offer email verification later.',
      emailPlaceholder: 'you@example.com',
      password: 'Password',
      passwordPlaceholder: 'At least 8 characters',
      organizationName: 'Franchise Workspace Name',
      organizationNamePlaceholder: 'Berlin Agency Franchise',
      organizationHint: 'This creates your first franchise workspace',
      createAccount: 'Create Account',
      createWithPasskey: 'Create with Passkey',
      newsletterOptIn: 'I want to receive the newsletter by email.',
      haveAccount: 'Already have an account?',
      signIn: 'Sign in',
      registrationFailed: 'Registration failed',
      passkeyRequired: 'Passkey setup is required to continue',
      passkeyTitle: 'Add a Passkey',
      passkeySubtitle: "Use your device's biometrics or security key for instant, passwordless sign-in.",
      setupPasskey: 'Set up Passkey',
      skipForNow: 'Skip for now',
    },
    verifyBanner: {
      title: 'Verify your email when you are ready',
      subtitle: 'Use {email} to receive verification and recovery emails later.',
      sendAction: 'Send verification email',
      sent: 'Verification email sent.',
      sendFailed: 'Could not send verification email.',
    },
  },
  sidebar: {
    dashboard: 'Dashboard',
    agents: 'Agents',
    usage: 'Usage',
    billing: 'Billing',
    settings: 'Settings',
    signOut: 'Sign Out',
  },
  dashboard: {
    home: {
      title: 'Dashboard',
      subtitle: 'Overview of your AI agents and usage',
      newAgent: 'New Agent',
      recentAgents: 'Recent Agents',
      viewAll: 'View all',
      noAgents: 'No agents yet',
      createFirstAgent: 'Create your first agent',
      tokenUsage30d: 'Token Usage (30d)',
    },
    stats: {
      totalTokens: 'Total Tokens',
      last30Days: 'Last 30 days',
      activeAgents: 'Active Agents',
      runningNow: 'Running now',
      requests: 'Requests',
    },
  },
  agents: {
    list: {
      title: 'Agents',
      agentCountSingular: '{count} agent',
      agentCountPlural: '{count} agents',
      newAgent: 'New Agent',
      newAgentTitle: 'New Agent',
      nameLabel: 'Name',
      namePlaceholder: 'Customer Support Bot',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'What does this agent do?',
      systemPromptLabel: 'System Prompt',
      systemPromptPlaceholder: 'You are a helpful assistant...',
      providerLabel: 'AI Provider',
      selectProvider: 'Select provider…',
      offeringPackLabel: 'Curated Offering Pack',
      offeringPackPlaceholder: 'Select offering pack…',
      createAgent: 'Create Agent',
      noAgents: 'No agents yet',
      createFirstAgent: 'Create your first agent',
    },
    detail: {
      activate: 'Activate',
      pause: 'Pause',
      tabChat: 'Chat',
      tabSettings: 'Settings',
      statusLabel: 'Agent is {status}',
      activateToChat: 'Activate the agent to start chatting',
      settingsTitle: 'Agent Settings',
      nameLabel: 'Name',
      descriptionLabel: 'Description',
      systemPromptLabel: 'System Prompt',
      systemPromptHint: "Instructions that define the agent's personality and behavior",
      providerLabel: 'AI Provider',
      noneOption: 'None',
      saveChanges: 'Save Changes',
      chatButton: 'Chat',
      offeringPackTitle: 'Curated Offering',
      offeringPackPlaceholder: 'Select offering pack…',
      applyOfferingPack: 'Apply Pack',
      channelBindingsTitle: 'Channel Bindings',
      channelBindingsPlaceholder: 'Select endpoint…',
      bindChannelButton: 'Bind Channel',
      noChannelBindings: 'No channel bindings yet',
      defaultBinding: 'Default',
      removeBinding: 'Remove',
    },
  },
  chat: {
    startConversationWith: 'Start a conversation with {agentName}',
    messagePlaceholder: 'Message {agentName}… (Enter to send, Shift+Enter for new line)',
    genericError: 'Unknown error',
    errorPrefix: 'Error',
  },
  settings: {
    index: {
      title: 'Settings',
      subtitle: 'Configure your franchise workspace and shared operations',
      modelProvidersTitle: 'Model Providers',
      modelProvidersDescription: 'Configure AI model providers and API keys',
      organizationTitle: 'Organization',
      organizationDescription: 'Manage current workspace identity, sector, and branding',
      workspacesTitle: 'Workspaces',
      workspacesDescription: 'Create franchise workspaces, switch context, and configure custom domains',
      channelsTitle: 'Channel Endpoints',
      channelsDescription: 'Create Telegram, WhatsApp, and phone endpoints from Shared Ops profiles',
      opsSecretsTitle: 'Shared Ops Secrets',
      opsSecretsDescription: 'Manage encrypted credentials for channels and hosting providers',
      opsHostingTitle: 'Shared Ops Hosting',
      opsHostingDescription: 'Manage Railway connections and sync deployment health',
      opsChannelsTitle: 'Shared Ops Channel Profiles',
      opsChannelsDescription: 'Publish reusable Telegram, WhatsApp, and phone channel profiles',
    },
    providers: {
      title: 'Model Providers',
      subtitle: 'Configure AI provider credentials for your agents',
      addProvider: 'Add Provider',
      addProviderTitle: 'Add Provider',
      displayName: 'Display Name',
      displayNamePlaceholder: 'My OpenAI Config',
      providerLabel: 'Provider',
      selectProvider: 'Select provider…',
      modelLabel: 'Model',
      selectModel: 'Select model…',
      apiKeyLabel: 'API Key',
      apiKeyPlaceholder: 'sk-…',
      apiKeyHint: 'Stored encrypted at rest',
      baseUrlLabel: 'Base URL',
      baseUrlPlaceholder: 'http://localhost:11434',
      setDefault: 'Set as default provider',
      saveProvider: 'Save Provider',
      noProviders: 'No providers configured',
      addProviderToEnable: 'Add a provider to enable AI agents',
    },
    organization: {
      title: 'Organization',
      subtitle: 'Manage active workspace settings',
      notMember: 'You are not part of an organization yet.',
      organizationName: 'Organization Name',
      organizationNamePlaceholder: 'Acme Corp',
      createOrganization: 'Create Organization',
      sectorLabel: 'Sector',
      saved: 'Saved!',
      organizationId: 'Organization ID',
      plan: 'Plan',
      sectors: {
        general: 'General',
        healthcare: 'Healthcare',
        finance: 'Finance',
        legal: 'Legal',
        logistics: 'Logistics',
        retail: 'Retail',
        education: 'Education',
        manufacturing: 'Manufacturing',
      },
    },
    workspaces: {
      title: 'Franchise Workspaces',
      subtitle: 'Manage agency-as-franchise workspaces and Shared Ops context',
      createTitle: 'Create Franchise Workspace',
      nameLabel: 'Workspace Name',
      namePlaceholder: 'Berlin Agency Franchise',
      sectorLabel: 'Sector',
      sectorPlaceholder: 'retail',
      createButton: 'Create Workspace',
      active: 'Active',
      switchButton: 'Switch',
      customDomainLabel: 'Custom Domain',
      customDomainPlaceholder: 'ai.your-franchise.com',
      themeAccentLabel: 'Theme Accent Color',
      themeAccentPlaceholder: '#0ea5e9',
      saveWorkspace: 'Save Workspace',
    },
    channels: {
      title: 'Channel Endpoints',
      subtitle: 'Instantiate franchise endpoints from Shared Ops channel profiles',
      createTitle: 'Create Endpoint',
      nameLabel: 'Endpoint Name',
      namePlaceholder: 'WhatsApp Intake',
      typeLabel: 'Channel Type',
      profileLabel: 'Shared Ops Profile',
      profilePlaceholder: 'Select channel profile…',
      configLabel: 'Endpoint Config (JSON)',
      createButton: 'Create Endpoint',
      invalidJson: 'Config must be valid JSON',
    },
    opsSecrets: {
      title: 'Shared Ops Secrets',
      subtitle: 'Encrypted credential vault for channels and hosting providers',
      createTitle: 'Add Secret',
      nameLabel: 'Secret Name',
      namePlaceholder: 'WhatsApp Access Token',
      providerLabel: 'Provider Key',
      providerPlaceholder: 'WHATSAPP_ACCESS_TOKEN',
      valueLabel: 'Secret Value',
      valuePlaceholder: 'Paste secret value',
      createButton: 'Save Secret',
      disableButton: 'Disable',
    },
    opsHosting: {
      title: 'Shared Ops Hosting',
      subtitle: 'Manage Railway-first hosting connections for franchise deployments',
      createTitle: 'Add Hosting Connection',
      nameLabel: 'Connection Name',
      namePlaceholder: 'Railway Production',
      projectLabel: 'Railway Project ID',
      projectPlaceholder: 'railway-project-id',
      environmentLabel: 'Railway Environment ID',
      environmentPlaceholder: 'railway-environment-id',
      secretLabel: 'Credential Secret',
      secretPlaceholder: 'Select secret…',
      createButton: 'Create Connection',
      syncButton: 'Sync',
    },
    opsChannels: {
      title: 'Shared Ops Channel Profiles',
      subtitle: 'Create reusable Telegram, WhatsApp, and phone runtime profiles',
      createTitle: 'Create Channel Profile',
      nameLabel: 'Profile Name',
      namePlaceholder: 'Global WhatsApp Profile',
      typeLabel: 'Channel Type',
      secretLabel: 'Credential Secret',
      secretPlaceholder: 'Select secret…',
      configLabel: 'Profile Config (JSON)',
      createButton: 'Create Profile',
      invalidJson: 'Config must be valid JSON',
    },
  },
  usage: {
    title: 'Usage Analytics',
    subtitle: 'Last 30 days',
    totalTokens: 'Total Tokens',
    totalRequests: 'Total Requests',
    estCost: 'Est. Cost',
    dailyTokenUsage: 'Daily Token Usage',
    byProvider: 'By Provider',
    providerLine: '{requests} requests · {cost}',
    loading: 'Loading usage data…',
    noUsage: 'No usage data yet',
  },
  billing: {
    title: 'Billing & Revenue Share',
    subtitle: 'Invoice-ready usage summaries by workspace and channel',
    generateTitle: 'Generate Invoice Period',
    periodStart: 'Period Start',
    periodEnd: 'Period End',
    revenueSharePct: 'Revenue Share (%)',
    generateButton: 'Generate Invoice',
    noInvoices: 'No invoices generated yet',
    totalTokens: 'Total Tokens',
    totalRequests: 'Total Requests',
    totalCost: 'Total Cost',
    revenueShare: 'Revenue Share',
  },
} as const satisfies TranslationDictionary
